import * as D from 'date-fns';
import * as Joi from 'joi';
import { CustomHelpers } from 'joi';
import { states } from '../consts/states.const';
import { RawSsn } from '../ssn-db';
import { formatISODate, isDateValueOnOrAfter, isDateValueOnOrBefore } from '../util';
import { CoverageTier, Gender, IdentificationCodeType, Relationship } from './enums';

export interface Member {
  id: string;
  externalEmployeeId: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  ssn: string;
  email?: string;
  phone?: string;
  isAddressSameAsPrimary?: boolean;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  relationshipCode: Relationship;
  benefitStartDate: string;
  benefitEndDate?: string;
  identificationCodeType?: IdentificationCodeType;
  gender?: Gender;
  fundingSource?: string;
  garnerCardIssuedAt?: string;
  isCancelTerminated?: boolean;
  isPastTerminated?: boolean;
}

export interface EditMember extends Partial<Member> {
  isNew?: boolean;
  id: string;
  relationshipCode: Relationship;
}

export interface ValidationContext {
  minBirthDate: string;
  maxBirthDate: string;
  minBenefitStartDate: string;
  maxBenefitStartDate?: string;
  minBenefitEndDate?: string;
  maxBenefitEndDate?: string;
  maxDependentBenefitEndDate: string;
  isSsnUnique(ssn: RawSsn, relationshipCode: Relationship): boolean | undefined;
  isNew?: boolean;
  primaryBenefitStartDate?: string;
  primaryBenefitEndDate?: string;
  isMembershipSingleSourceOfTruthInEligibility?: boolean;
}

// These refer to properties in MemberGroup
const primaryBenefitStartDate = Joi.ref('$primaryBenefitStartDate');
primaryBenefitStartDate.display = 'employee benefit start date';

const primaryBenefitEndDate = Joi.ref('$primaryBenefitEndDate');
primaryBenefitEndDate.display = 'employee benefit end date';

const maxDependentBenefitEndDate = Joi.ref('$maxDependentBenefitEndDate', { render: true });
const minBenefitStartDate = Joi.ref('$minBenefitStartDate', { render: true });
const maxBenefitStartDate = Joi.ref('$maxBenefitStartDate', { render: true });
const minBenefitEndDate = Joi.ref('$minBenefitEndDate', { render: true });
const maxBenefitEndDate = Joi.ref('$maxBenefitEndDate', { render: true });
const relationshipCode = Joi.ref('relationshipCode', { render: true });

const thisBenefitStartDate = Joi.ref('benefitStartDate');
thisBenefitStartDate.display = 'Benefit Start Date';
const thisAddressLine1 = Joi.ref('addressLine1');
thisAddressLine1.display = 'Address Line 1';

// https://ihateregex.io/expr/ssn/
const ssnRegex = /^(?!0{3})(?!6{3})[0-8]\d{2}(?!0{2})\d{2}(?!0{4})\d{4}$|^(9\d{2})([7]\d|8[0-8])(\d{4})$/;
const invalidSSNList = ['000000000', '111111111', '222222222', '333333333', '123456789'];
const zipCodeRegex = /^\d{5}(-\d{4})?$/;
const zipCodeErrorMessage = '{#label} must be a XXXXX or XXXXX-XXXX';

function getRequiredTerminationMessages(subject: string): Joi.LanguageMessages {
  const message =
    `Employee coverage tier does not cover ${subject} and they must ` +
    `be terminated on or before {$maxDependentBenefitEndDate}`;

  return { 'any.required': message, 'date.max': message };
}

function isInvalidSSN(ssn: string) {
  return (
    !ssnRegex.test(ssn) ||
    invalidSSNList.includes(ssn) ||
    ssn.substring(0, 3) === '000' ||
    ssn.substring(0, 3) === '666' ||
    ssn.substring(3, 5) === '00' ||
    ssn.substring(5, 9) === '0000'
  );
}

function validateSSN(value: any, helpers: CustomHelpers<any>, isPrimary: boolean) {
  if (!value) {
    if (isPrimary || !helpers.prefs.context?.isMembershipSingleSourceOfTruthInEligibility) {
      return helpers.error('ssn.is_empty');
    } else {
      helpers.warn('ssn.dependent_is_empty');
    }
  } else {
    if (isInvalidSSN(value)) {
      const messageCode = 'ssn.invalid_format';
      if (helpers.prefs.context?.isNew || helpers.prefs.context?.isMembershipSingleSourceOfTruthInEligibility) {
        return helpers.error(messageCode);
      }
      helpers.warn(messageCode);
    }

    if (helpers.prefs.context?.isSsnUnique?.(value, getRelationshipCode(helpers, relationshipCode)) === false) {
      const messageCode = 'ssn.not_unique';
      if (helpers.prefs.context?.isNew || helpers.prefs.context?.isMembershipSingleSourceOfTruthInEligibility) {
        return helpers.error(messageCode);
      }
      helpers.warn(messageCode);
    }
  }

  return value;
}

const memberSchemaBody = {
  id: Joi.required(),
  externalEmployeeId: Joi.string(),

  //#region Demographics

  firstName: Joi.string().trim().required().label('First Name'),

  lastName: Joi.string().trim().required().label('Last Name'),

  birthDate: Joi.date()
    .empty('')
    .iso()
    .raw()
    .required()
    .$.min(Joi.ref('$minBirthDate'))
    .max(Joi.ref('$maxBirthDate'))
    .message('{#label} must be between {$minBirthDate} and {$maxBirthDate}')
    .label('Birth Date'),

  ssn: Joi.string()
    .empty('')
    .required()
    .custom((value, helpers) => {
      if (isInvalidSSN(value)) {
        const messageCode = 'ssn.invalid_format';
        if (helpers.prefs.context?.isNew) {
          return helpers.error(messageCode);
        }
        helpers.warn(messageCode);
      }

      if (helpers.prefs.context?.isSsnUnique?.(value, getRelationshipCode(helpers, relationshipCode)) === false) {
        const messageCode = 'ssn.not_unique';
        if (helpers.prefs.context?.isNew) {
          return helpers.error(messageCode);
        }
        helpers.warn(messageCode);
      }

      return value;
    }, 'Valid SSN')
    .label('SSN or ITTN'),

  //#endregion

  //#region Contact Info

  email: Joi.string().email().allow('').label('Email'),

  phone: Joi.string()
    .empty('')
    .regex(/^\d{10,15}$/)
    .message('{#label} must be 10-15 digits without dashes')
    .label('Phone Number'),

  //#endregion

  //#region Address

  isAddressSameAsPrimary: Joi.boolean()
    .alter({
      primary: schema => schema.not(Joi.exist()),
    })
    .label('Same address as employee?'),

  addressLine1: Joi.string()
    .trim()
    .empty('')
    .alter({
      primary: schema => schema.required(),
      dependent: schema =>
        schema.when('isAddressSameAsPrimary', {
          is: Joi.not(true),
          then: Joi.required(),
        }),
    })
    .label('Address'),

  addressLine2: Joi.string()
    .trim()
    .empty('')
    .when(thisAddressLine1, {
      is: Joi.exist().empty(''),
      then: Joi.not(thisAddressLine1),
    })
    .label('Address line 2'),

  city: Joi.string().trim().empty('').label('City'),

  state: Joi.valid(...states)
    .messages({ 'any.valid': '{#label} must be a valid two-letter abbreviation' })
    .label('State'),

  zipCode: Joi.string()
    .empty('')
    .alter({
      primary: schema => schema.regex(zipCodeRegex).message(zipCodeErrorMessage),
      dependent: schema =>
        schema.when('isAddressSameAsPrimary', {
          is: Joi.not(true),
          then: Joi.string().regex(zipCodeRegex).message(zipCodeErrorMessage),
        }),
    })
    .label('Zip Code'),

  //#endregion

  //#region Benefit Info

  relationshipCode: Joi.required()
    .valid(Relationship.SELF, Relationship.CHILD, Relationship.SPOUSE)
    .alter({
      primary: schema => schema.valid(Relationship.SELF),
      dependent: schema => schema.valid(Relationship.CHILD, Relationship.SPOUSE),
    })
    .label('Relationship Code'),

  benefitStartDate: Joi.date()
    .empty('')
    .iso()
    .raw()
    .required()
    .when('relationshipCode', {
      is: Relationship.SELF,
      // Primary members must always have dates within plan range
      then: Joi.date().custom(validateDateBetween(minBenefitStartDate, maxBenefitStartDate)),
      // Dependents skip validation if terminated
      otherwise: Joi.date().when('isPastTerminated', {
        is: true,
        then: Joi.date(),
        otherwise: Joi.date().when('isCancelTerminated', {
          is: true,
          then: Joi.date(),
          otherwise: Joi.date()
            .when('relationshipCode', {
              is: Relationship.SPOUSE,
              then: Joi.when('/coverageTier', {
                is: Joi.equal(CoverageTier.EMPLOYEE, CoverageTier.EMPLOYEE_CHILDREN),
                then: Joi.date().custom(validateDateBetweenFullRange),
                otherwise: Joi.date().custom(validateDateBetweenPrimaryAndEnd),
              }),
            })
            .when('relationshipCode', {
              is: Relationship.CHILD,
              then: Joi.when('/coverageTier', {
                is: Joi.equal(CoverageTier.EMPLOYEE, CoverageTier.EMPLOYEE_SPOUSE),
                then: Joi.date().custom(validateDateBetweenFullRange),
                otherwise: Joi.date().custom(validateDateBetweenPrimaryAndEnd),
              }),
            }),
        }),
      }),
    })
    .label('Benefit Start Date'),

  benefitEndDate: Joi.date()
    .empty('')
    .iso()
    .raw()
    .greater(thisBenefitStartDate)
    .when('relationshipCode', {
      is: Relationship.SELF,
      // Primary members must always have dates within plan range
      then: Joi.date().custom(validateDateBetween(minBenefitEndDate, maxBenefitEndDate)),
    })
    .alter({
      dependent: schema =>
        schema
          .when(primaryBenefitEndDate, {
            is: Joi.exist().empty(''),
            then: Joi.when('isCancelTerminated', {
              switch: [
                {
                  is: true,
                  then: Joi.date(),
                },
                {
                  is: false,
                  then: Joi.date().required().max(primaryBenefitEndDate),
                },
              ],
            }),
          })
          .when('/coverageTier', {
            switch: [
              {
                is: CoverageTier.EMPLOYEE,
                then: Joi.when('isCancelTerminated', {
                  switch: [
                    {
                      is: true,
                      then: Joi.date(),
                    },
                    {
                      is: false,
                      then: Joi.date()
                        .required()
                        .max(maxDependentBenefitEndDate)
                        .messages(getRequiredTerminationMessages('dependents')),
                    },
                  ],
                }),
              },
              {
                is: CoverageTier.EMPLOYEE_SPOUSE,
                then: Joi.when('relationshipCode', {
                  is: Relationship.CHILD,
                  then: Joi.when('isCancelTerminated', {
                    switch: [
                      {
                        is: true,
                        then: Joi.date(),
                      },
                      {
                        is: false,
                        then: Joi.date()
                          .required()
                          .max(maxDependentBenefitEndDate)
                          .messages(getRequiredTerminationMessages('children')),
                      },
                    ],
                  }),
                }),
              },
              {
                is: CoverageTier.EMPLOYEE_CHILDREN,
                then: Joi.when('relationshipCode', {
                  is: Relationship.SPOUSE,
                  then: Joi.when('isCancelTerminated', {
                    switch: [
                      {
                        is: true,
                        then: Joi.date(),
                      },
                      {
                        is: false,
                        then: Joi.date()
                          .required()
                          .max(maxDependentBenefitEndDate)
                          .messages(getRequiredTerminationMessages('spouses')),
                      },
                    ],
                  }),
                }),
              },
            ],
          }),
    })
    .label('Benefit End Date'),
  isCancelTerminated: Joi.boolean(),
  isPastTerminated: Joi.boolean(),
  //#endregion
};

export const memberSchema = Joi.object<Member>(memberSchemaBody)
  .and('addressLine1', 'city', 'state', 'zipCode')
  .with('addressLine2', 'addressLine1')
  .messages({
    'object.and': 'Incomplete address provided',
    'object.with': 'Use first line of address',
    'date.min': '{#label} must be on or after {#limit}',
    'date.max': '{#label} must be on or before {#limit}',
    'date.between': '{#label} must be between {#start} and {#end}',
    'date.greater':
      "{#label} cannot precede Benefit Start Date. If this member should not be eligible for this plan year, please reach out to your Account Manager to remove this member's coverage.",
    'ssn.invalid_format': '{#label} must be a valid 9-digit SSN or ITTN without dashes',
    'ssn.not_unique': '{#label} must be unique',
    'ssn.is_empty': '{#label} is empty',
  });

export const memberSchemaV2 = Joi.object<Member>({
  ...memberSchemaBody,
  ssn: Joi.any()
    .alter({
      primary: schema =>
        schema.required().custom((value, helpers) => {
          return validateSSN(value, helpers, true);
        }, 'Valid SSN'),
      dependent: schema =>
        schema.custom((value, helpers) => {
          return validateSSN(value, helpers, false);
        }, 'Valid SSN'),
    })
    .label('SSN or ITTN'),
})
  .and('addressLine1', 'city', 'state', 'zipCode')
  .with('addressLine2', 'addressLine1')
  .messages({
    'object.and': 'Incomplete address provided',
    'object.with': 'Use first line of address',
    'date.min': '{#label} must be on or after {#limit}',
    'date.max': '{#label} must be on or before {#limit}',
    'date.between': '{#label} must be between {#start} and {#end}',
    'date.greater':
      "{#label} cannot precede Benefit Start Date. If this member should not be eligible for this plan year, please reach out to your Account Manager to remove this member's coverage.",
    'ssn.invalid_format': '{#label} must be a valid 9-digit SSN or ITTN without dashes',
    'ssn.not_unique': '{#label} must be unique',
    'ssn.is_empty': '{#label} is empty',
    'ssn.dependent_is_empty':
      'Dependent SSN field is empty. If a dependent wishes to sign up for their own Garner account, an SSN must be saved on their record for authentication.',
  });

declare module 'joi' {
  interface Reference {
    resolve(value: unknown, state: unknown, prefs: unknown, local?: unknown, options?: unknown): any;
  }
}

function validateDateBetween(startDateRef: Joi.Reference, endDateRef: Joi.Reference): Joi.CustomValidator<Date> {
  return (rawValue, helpers) => {
    const value = D.parseISO(rawValue.toISOString().split('T')[0]);
    const startDate = getDateFromRef(helpers, startDateRef);
    const endDate = getDateFromRef(helpers, endDateRef);

    const isValueOnOrAfterStartDate = isDateValueOnOrAfter(value, startDate);
    const isValueOnOrBeforeEndDate = isDateValueOnOrBefore(value, endDate);

    if (startDate && endDate) {
      return isValueOnOrAfterStartDate && isValueOnOrBeforeEndDate
        ? value
        : helpers.error('date.between', {
            start: formatISODate(startDate),
            end: formatISODate(endDate),
          });
    } else if (startDate) {
      return isValueOnOrAfterStartDate ? value : helpers.error('date.min', { limit: formatISODate(startDate) });
    } else if (endDate) {
      return isValueOnOrBeforeEndDate ? value : helpers.error('date.max', { limit: formatISODate(endDate) });
    }

    return value;
  };
}

function getDateFromRef(helpers: Joi.CustomHelpers, ref: Joi.Reference): Date | undefined {
  const resolved = ref.resolve(helpers.original, helpers.state, helpers.prefs);
  if (!resolved) return undefined;
  const date = D.parseISO(resolved);
  return D.isValid(date) ? date : undefined;
}

function getRelationshipCode(helpers: Joi.CustomHelpers, ref: Joi.Reference): Relationship | undefined {
  return ref.resolve(helpers.original, helpers.state, helpers.prefs) ?? undefined;
}

function validateDateBetweenFullRange(rawValue: Date, helpers: Joi.CustomHelpers) {
  const value = D.parseISO(rawValue.toISOString().split('T')[0]);
  if (helpers.prefs.context?.isMembershipSingleSourceOfTruthInEligibility) {
    const fn = validateDateBetween(minBenefitStartDate, maxBenefitStartDate);
    return fn(rawValue, helpers);
  }
  return value;
}

function validateDateBetweenPrimaryAndEnd(rawValue: Date, helpers: Joi.CustomHelpers) {
  const value = D.parseISO(rawValue.toISOString().split('T')[0]);
  if (helpers.prefs.context?.isMembershipSingleSourceOfTruthInEligibility) {
    const endDate = getDateFromRef(helpers, maxBenefitStartDate);
    const startDate = getDateFromRef(helpers, minBenefitEndDate);

    const isValueOnOrAfterStartDate = isDateValueOnOrAfter(value, startDate);
    const isValueOnOrBeforeEndDate = isDateValueOnOrBefore(value, endDate);

    if (isValueOnOrAfterStartDate && isValueOnOrBeforeEndDate) {
      const fn = validateDateBetween(primaryBenefitStartDate, maxBenefitStartDate);
      return fn(rawValue, helpers);
    }
  }
  return value;
}

export const primaryMemberSchema = memberSchema.tailor('primary');
export const dependentMemberSchema = memberSchema.tailor('dependent');
export const dependentMemberSchemaV2 = memberSchemaV2.tailor('dependent');
